import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface AiResponse {
  queryType: string;
  answer: string;
  data: Record<string, unknown> | null;
  suggestions: string[];
}

interface RecentQuery {
  query: string;
  response: AiResponse;
  timestamp: number;
}

const MAX_RECENT = 5;

export function useAiQuery(projectId: string) {
  const [recentQueries, setRecentQueries] = useState<RecentQuery[]>([]);

  const mutation = useMutation({
    mutationFn: async (query: string) => {
      const { data } = await api.post<AiResponse>(
        `/projects/${projectId}/ai/query`,
        { query },
      );
      return data;
    },
    onSuccess: (data, query) => {
      setRecentQueries((prev) => {
        const updated = [
          { query, response: data, timestamp: Date.now() },
          ...prev.filter((r) => r.query !== query),
        ];
        return updated.slice(0, MAX_RECENT);
      });
    },
  });

  const clearRecent = useCallback(() => setRecentQueries([]), []);

  return {
    query: mutation.mutate,
    queryAsync: mutation.mutateAsync,
    data: mutation.data ?? null,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    recentQueries,
    clearRecent,
  };
}
